import { apiAnalytics, ANALYTICS_EVENTS, EVENTS_MAPPING } from '@api/analytics';
import { apiCart } from '@api/cart';
import {
  getQuizCompletedStorageKey,
  QuizAnalyticsContext,
  QuizQuestionAnalytics,
} from '@helpers/quiz-analytics';
import { SessionInfoResponse } from '@models/user';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useUserStore } from './user';

export type AnalyticProduct = {
  idp: number;
  idv?: number;
  item_name: string;
  price: number;
  quantity: number;
  item_id?: string
  item_brand?: string
  item_category?: string
  item_list_id?: string
  item_list_name?: string
  index?: number
};

export const useAnalyticsStore = defineStore('analytics', () => {
  const userStore = useUserStore();

  const aid = ref(localStorage.getItem('aid'));
  const cid = ref(localStorage.getItem('cid'));
  const campaign = ref(localStorage.getItem('campaign'));
  const utmSource = ref(localStorage.getItem('utmSource'));
  const utmMedium = ref(localStorage.getItem('utmMedium'));
  const fbc = ref(localStorage.getItem('fbc'));
  const efTransactionId = ref(localStorage.getItem('_ef_transaction_id'));

  const facebookUserData = computed(() => {
    const data = {
      fbc: fbc.value,
      external_id: <number | null> null,
      em: <string | null> null,
      fn: <string | null> null,
      ln: <string | null> null,
      db: <number | null> null,
      ph: <string | null> null,
      ge: <string | null> null,
      country: <string | null> null,
      st: <string | null> null,
      zp: <string | null> null,
    };

    const userProfile = userStore.profileInfo?.user;

    if (userProfile) {
      const addr = userProfile.addresses.shippingAddress || userProfile.addresses.billingAddress;

      data.external_id = userProfile.id;
      data.em = userProfile.email.toLowerCase();
      data.fn = userProfile.firstName?.toLowerCase() || null;
      data.ln = userProfile.lastName?.toLowerCase() || null;
      data.db = userProfile.dob;
      data.ph = userProfile.phone?.replace('+', '') || null;
      data.ge = userProfile.gender?.toLowerCase() || null;
      data.country = (addr?.countryCode || 'us').toLowerCase();
      data.st = addr?.administrativeArea ? addr?.administrativeArea.toLowerCase() : null;
      data.zp = addr?.postalCode || null;
    }

    return data;
  });

  // Fire on the first page of the quiz
  const viewContent = (quizName: string) => {
    // eslint-disable-next-line no-console
    console.log('viewContent', { quizName });

    window.ttq?.track('ViewContent', { content_name: quizName });
  };

  const sendQuizAnalyticsEvent = (
    eventName: ANALYTICS_EVENTS.QuizStarted | ANALYTICS_EVENTS.QuizStepCompleted | ANALYTICS_EVENTS.QuizCompleted | ANALYTICS_EVENTS.ViewQuizStep,
    payload: QuizAnalyticsContext & { quiz_step?: number; quiz_questions?: QuizQuestionAnalytics[]; stepId?: number|string; stepSlug?: string; stepName?: string },
  ) => {
    const eventId = crypto.randomUUID();

    if (eventName === ANALYTICS_EVENTS.QuizStepCompleted) {
      apiAnalytics.trackQuizStepCompleted({
        ...payload,
        eventId,
        quiz_step: payload.quiz_step ?? 0,
        quiz_questions: payload.quiz_questions ?? [],
      });
    } else if (eventName === ANALYTICS_EVENTS.QuizStarted) {
      apiAnalytics.trackQuizStarted({ ...payload, eventId });
    } else if (eventName === ANALYTICS_EVENTS.ViewQuizStep) {
      apiAnalytics.trackViewQuizStep({
        ...payload,
        eventId,
        stepId: payload.stepId ?? '',
        stepSlug: payload.stepSlug ?? '',
        stepName: payload.stepName,
        quiz_name: payload.quiz_name ?? '',
        quiz_category: payload.quiz_category ?? '',
        quiz_id: payload.quiz_id ?? '',
      });
    } else {
      apiAnalytics.trackQuizCompleted({ ...payload, eventId });
    }
  };

  const trackQuizStarted = (payload: QuizAnalyticsContext) => {
    sendQuizAnalyticsEvent(ANALYTICS_EVENTS.QuizStarted, payload);
  };

  const trackQuizStepCompleted = (
    payload: QuizAnalyticsContext & { quiz_step: number; quiz_questions: QuizQuestionAnalytics[] },
  ) => {
    sendQuizAnalyticsEvent(ANALYTICS_EVENTS.QuizStepCompleted, payload);
  };

  const trackQuizCompleted = (payload: QuizAnalyticsContext) => {
    const storageKey = getQuizCompletedStorageKey(payload.quiz_id);
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    sessionStorage.setItem(storageKey, '1');
    sendQuizAnalyticsEvent(ANALYTICS_EVENTS.QuizCompleted, payload);
  };

  const trackViewQuizStep = (
    payload: QuizAnalyticsContext & { stepId: number|string; stepSlug: string; stepName?: string; quiz_step?: number; quiz_questions?: QuizQuestionAnalytics[] },
  ) => {
    sendQuizAnalyticsEvent(ANALYTICS_EVENTS.ViewQuizStep, payload);
  };

  const selectQuizAnswer = (
    answerId: number[],
    answer: string[],
    stepId: number|string,
    stepSlug: string,
  ) => {
    // eslint-disable-next-line no-console
    console.log(ANALYTICS_EVENTS.SelectQuizAnswer, { answer, stepSlug });
    const eventUuid = crypto.randomUUID();

    apiAnalytics.trackSelectQuizAnswer({
      stepSlug: stepSlug,
      stepValue: answer,
      stepId: stepId,
      answerId: answerId,
      eventId: eventUuid,
    });
  };

  // Fire when the user looked at the pharmacy
  const viewPharmacy = (
    categoryId: number,
    categoryHandle: string,
    categoryName: string,
    items: AnalyticProduct[],
  ) => {
    // eslint-disable-next-line no-console
    console.log('viewPharmacy', { categoryHandle, categoryName, items });
  };

  // Fire when User has registered
  const createAccount = (info: SessionInfoResponse) => {
    // eslint-disable-next-line no-console
    console.log('createAccount', { aid: aid.value });
    // eslint-disable-next-line no-console
    console.log('createAccount', { cid: cid.value });
    // eslint-disable-next-line no-console
    console.log('createAccount', { campaign: campaign.value });
  };

  // After you add a product to the cart (either subscription or product)
  const addToCart = (
    items: AnalyticProduct[],
    value = 0,
    eventId: string|null = null,
  ) => {
    // eslint-disable-next-line no-console
    console.log('addToCart', { items, value, eventId });

    window.ttq?.track('AddToCart', {
      content_ids: items.map((item) => item.item_name),
      content_type: 'product',
      currency: 'USD',
      value,
    });

    apiCart.trackAddToCart({
      eventId: eventId || '',
      currency: 'USD',
      value,
      items: items.map((item) => ({
        id: item.item_id,
        name: item.item_name,
        category: item.item_list_id,
        price: item.price,
        quantity: item.quantity,
      })),
    });
  };

  // After you remove a product to the cart  - click on minus
  const removeFromCart = (items: AnalyticProduct[], value = 0, eventId: string|null = null)  => {
    // eslint-disable-next-line no-console
    console.log('removeFromCart', { items, value });
  };

  // When checkout page is opened
  const beginCheckout = (
    items: AnalyticProduct[],
    value = 0,
    cartId: number|null = null,
  ) => {
    // eslint-disable-next-line no-console
    console.log('beginCheckout', { items, value });

    window.ttq?.track('InitiateCheckout', {
      content_ids: items.map((item) => item.item_name),
      content_type: 'product',
      currency: 'USD',
      num_items: items.length,
      value,
    });
  };

  // Process queued track events from window.trackEvents.
  const processTrackEvents = () => {
    for (const { event, payload } of window.trackEvents || []) {
      switch(event) {
        case ANALYTICS_EVENTS.ProductView:
          apiAnalytics.trackProductView(payload || {});
          break;
        default:
          break;
      }
    }
    window.trackEvents = [];
  };

  // After a successful purchase
  const purchase = (items: AnalyticProduct[], transactionId: string, value = 0, cartId: number) => {
    // eslint-disable-next-line no-console
    console.log('purchase', { items, transactionId, cartId, value, aid: aid.value });

    window.ttq?.track('Purchase', {
      content_ids: items.map((item) => item.item_name),
      content_type: 'product',
      currency: 'USD',
      num_items: items.length,
      value,
    });

    window.VWO?.event('purchase', {
      purchaseValue: value,
    });
  };

  return {
    aid,
    campaign,
    fbc,
    efTransactionId,
    facebookUserData,
    utmSource,
    utmMedium,
    viewContent,
    createAccount,
    addToCart,
    beginCheckout,
    processTrackEvents,
    purchase,
    viewPharmacy,
    removeFromCart,
    selectQuizAnswer,
    trackQuizStarted,
    trackQuizStepCompleted,
    trackQuizCompleted,
    trackViewQuizStep,
  };
});
